/* Web Push subscription helper.
 *
 * Wraps the browser's PushManager in a small async API:
 *   getStatus()              → "supported" | "denied" | "blocked-by-os" | "unsupported"
 *   isSubscribed()           → boolean
 *   subscribe(location)      → posts the PushSubscription to /api/push/subscribe
 *   unsubscribe()            → tears down the subscription on both ends
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const safe = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getServiceWorker() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function isSubscribed() {
  const reg = await getServiceWorker();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

export async function subscribePush(location) {
  if (!pushSupported()) throw new Error("Push not supported on this device.");
  // 1. ask browser permission
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied" ? "Permission denied." : "Permission not granted.");
  }
  // 2. fetch our public VAPID key
  const cfg = await axios.get(`${API}/push/config`);
  const vapidKey = cfg.data?.vapid_public_key;
  if (!vapidKey) throw new Error("Push is not configured on the server.");
  // 3. tell the browser to register a push subscription
  const reg = await getServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }
  // 4. ship the subscription to the backend
  const json = sub.toJSON();
  await axios.post(
    `${API}/push/subscribe`,
    {
      endpoint: json.endpoint,
      keys: json.keys,
      location: location
        ? {
            name: location.name,
            admin1: location.admin1 || null,
            country: location.country || null,
            latitude: Number(location.latitude),
            longitude: Number(location.longitude),
          }
        : null,
    },
    { withCredentials: true },
  );
  return sub;
}

export async function unsubscribePush() {
  const reg = await getServiceWorker();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await axios.post(
      `${API}/push/unsubscribe`,
      { endpoint },
      { withCredentials: true },
    );
  } catch {
    /* ignore — local unsubscribe already done */
  }
  return true;
}
